import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { User, UserAuthResponse, UserAuthSuccess } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private user = new BehaviorSubject<UserAuthResponse | null>(null);
  user$ = this.user.asObservable();
  isLoggedIn$ = this.user$.pipe(map((user) => !!user));

  constructor(private httpClient: HttpClient) {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const tokenExpires = localStorage.getItem('tokenExpires');
    const refreshExpires = localStorage.getItem('refreshExpires');
    const userRaw = localStorage.getItem('loggedUser');

    if (token && refreshToken && tokenExpires && refreshExpires && userRaw) {
      const user = JSON.parse(userRaw);
      const hydrated: UserAuthResponse = {
        token,
        refreshToken,
        tokenExpires: new Date(tokenExpires),
        refreshExpires: new Date(refreshExpires),
        success: true,
        user: {
          email: user.email,
          id: user.id,
          isEmailVerified: user.isEmailVerified,
          name: user.name,
          role: user.role,
        },
      };
      this.user.next(hydrated);
    }
  }

  login(email: string, password: string): Observable<UserAuthResponse> {
    return this.httpClient
      .post<any>(`${environment.baseServerUrl}auth/login`, {
        email,
        password,
      })
      .pipe(
        map((response) => {
          const res: UserAuthSuccess = response;
          const userResponse: UserAuthResponse = {
            refreshExpires: new Date(res.tokens.refresh.expires),
            refreshToken: res.tokens.refresh.token,
            success: true,
            token: res.tokens.access.token,
            tokenExpires: new Date(res.tokens.access.expires),
            user: {
              email: res.user.email,
              id: res.user.id,
              isEmailVerified: res.user.isEmailVerified,
              name: res.user.name,
              role: res.user.role,
            },
          };

          this.user.next(userResponse);
          this.setTokens(res);
          return userResponse;
        })
      );
  }

  setTokens(res: UserAuthSuccess) {
    localStorage.setItem('token', res.tokens.access.token);
    localStorage.setItem('refreshToken', res.tokens.refresh.token);
    localStorage.setItem(
      'tokenExpires',
      new Date(res.tokens.access.expires).toISOString()
    );
    localStorage.setItem(
      'refreshExpires',
      new Date(res.tokens.refresh.expires).toISOString()
    );
    localStorage.setItem('loggedUser', JSON.stringify(res.user));
  }

  forgotPassword(email: string): Observable<HttpResponse<any>> {
    return this.httpClient.post<any>(
      `${environment.baseServerUrl}auth/forgot-password`,
      { email }
    );
  }

  resetPassword(token: string, password: string) {
    return this.httpClient.post<any>(
      `${environment.baseServerUrl}auth/reset-password?token=${token}`,
      { password }
    );
  }

  register(userInfo: {
    name: string;
    email: string;
    password: string;
  }): Observable<any> {
    return this.httpClient
      .post(`${environment.baseServerUrl}auth/register`, userInfo)
      .pipe(map((response) => response));
  }

  getUsers(by: string): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.baseServerUrl}users?role=${by}&limit=10&page=1`
    );
  }

  getLoggedUser(): Observable<UserAuthSuccess | null> {
    const user = this.getCurrentUser();
    if (!user) return of(null);

    return this.httpClient.get<any>(
      `${environment.baseServerUrl}users/${user.id}`,
      {
        headers: new HttpHeaders().set(
          'Authorization',
          `Bearer ${localStorage.getItem('token')}`
        ),
      }
    );
  }

  getCurrentUser(): User {
    const loggedUser = localStorage.getItem('loggedUser');
    return loggedUser ? JSON.parse(loggedUser) : null;
  }

  refreshAccessToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    return this.httpClient
      .post<any>(`${environment.baseServerUrl}auth/refresh-tokens`, {
        refreshToken,
      })
      .pipe(
        tap((response) => {
          const existingUser = localStorage.getItem('loggedUser') ?? '{}';
          const formattedResponse: UserAuthSuccess = {
            user: JSON.parse(existingUser),
            tokens: response,
          };
          this.setTokens(formattedResponse);
        }),
        catchError((error) => {
          this.cleanupUserInfo();
          throw error;
        })
      );
  }

  logout(): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    this.cleanupUserInfo();
    this.user.next(null);
    return this.httpClient.post<any>(
      `${environment.baseServerUrl}auth/logout`,
      { refreshToken },
      {
        headers: new HttpHeaders().set(
          'Authorization',
          `Bearer ${localStorage.getItem('token')}`
        ),
      }
    );
  }

  private cleanupUserInfo() {
    const keys = [
      'refreshToken',
      'token',
      'tokenExpires',
      'refreshExpires',
      'activeBatch',
      'activeClasses',
      'loggedUser',
    ];
    keys.forEach((key) => localStorage.removeItem(key));
  }
}
